<template>
    <div class="overflow-hidden rounded-card border border-panel-border bg-white">
        <div class="flex flex-wrap items-center justify-between gap-2 bg-panel px-4 py-1.5">
            <h4 class="!mb-0 text-xs !font-bold">
                {{ trans('workorder.impact_assessment.title') }}
                <em v-if="!readonly" class="text-danger">*</em>
            </h4>
            <span v-if="levelLabel" :class="badgeClasses">
                {{ levelLabel }}
            </span>
        </div>

        <div class="px-4 py-3">
            <p class="!mb-2.5 text-[14px]"><span class="me-1 font-bold">1.</span>{{ trans('workorder.impact_assessment.q1') }}</p>
            <yes-no-selector
                :model-value="value.outage_impact_within_hour"
                :disabled="readonly"
                size="small"
                v-bind="invertedSelectedClasses"
                test-id="impact-q1"
                @update:model-value="onQ1Update"
            />

            <div v-if="value.outage_impact_within_hour === true" class="mt-5">
                <p class="!mb-2.5 text-[14px]"><span class="me-1 font-bold">2.</span>{{ trans('workorder.impact_assessment.q2') }}</p>
                <yes-no-selector
                    :model-value="value.wtsl_target_exceeded"
                    :disabled="readonly"
                    size="small"
                    v-bind="invertedSelectedClasses"
                    test-id="impact-q2"
                    @update:model-value="updateField('wtsl_target_exceeded', $event)"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import YesNoSelector from '@/Components/YesNoSelector.vue';
import { impactAssessmentLevel } from '@/Pages/WorkOrders/Modals/impactAssessment.js';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    modelValue: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const value = computed(() => props.modelValue ?? {});

// "Yes" indicates a worse outcome on Q1/Q2, so the selected colors flip vs. the YesNoSelector defaults.
const invertedSelectedClasses = {
    yesSelectedClass: 'bg-danger-subtle border-danger-subtle text-danger-emphasis',
    noSelectedClass: 'bg-success-subtle border-success-subtle text-success-emphasis',
};

const level = computed(() => impactAssessmentLevel(value.value));

const levelLabel = computed(() => (level.value ? trans(`workorder.impact_assessment.level.${level.value}`) : null));

const badgeClasses = computed(() => {
    const base = 'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold';
    if (level.value === 1) return `${base} bg-danger-subtle text-danger-emphasis`;
    if (level.value === 2) return `${base} bg-amber-100 text-amber-800`;
    return `${base} bg-success-subtle text-success-emphasis`;
});

const updateField = (key, val) => {
    emit('update:modelValue', { ...value.value, [key]: val });
};

const onQ1Update = (val) => {
    const next = { ...value.value, outage_impact_within_hour: val };
    if (val !== true) {
        delete next.wtsl_target_exceeded;
    }
    emit('update:modelValue', next);
};
</script>
